import {
  AiRedoIcon,
  DownloadIcon,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  Tooltip,
  TrashDeleteIcon,
  SupportTvIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusSeenChipSelect } from '@/components/StatusSeenChipSelect';
import { CLIP_REVIEW_STATES } from '@/constants';
import { TClip } from '@/features/clips/types';

type TMonitoringContentMiniCardActionsProps = {
  item: TClip;
  size?: IconButtonSizeEnum;
  className?: string;
  iconButtonColor?: IconButtonColorEnum;
  onGoToProgram?: () => void;
  onReview?: (id: string) => void;
  onDownload?: (id: string) => void;
  onRelaunch?: (id: string) => void;
  onDelete?: (id: string) => void;
};

export function ClipMiniCardCardActions({
  item,
  className,
  size,
  iconButtonColor = IconButtonColorEnum.NONE_DEFAULT,
  onGoToProgram,
  onReview,
  onDelete,
  onDownload,
  onRelaunch,
}: TMonitoringContentMiniCardActionsProps) {
  const { t } = useTranslation();

  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);

  const handleClickReview = (event: { stopPropagation: () => void }) => {
    setIsSelectOpen(!isSelectOpen);
    event.stopPropagation();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div
        className={clsx('flex items-center p-size-4', className)}
        onClick={event => {
          event.stopPropagation();
        }}
      >
        {onGoToProgram && (
          <Tooltip help={t('Ir al programa')} grow>
            <IconButton size={size} color={iconButtonColor} onClick={onGoToProgram}>
              <SupportTvIcon size={IconSizeEnum.SM} className="relative -top-[1px]" />
            </IconButton>
          </Tooltip>
        )}
        {onReview && (
          <Tooltip help={t('Revisión')} grow>
            <div ref={selectRef}>
              <IconButton
                className="relative top-0.5"
                size={size}
                color={iconButtonColor}
                onClick={handleClickReview}
              >
                <StatusSeenChipSelect
                  asIcon
                  value={item.review_state}
                  onChange={onReview}
                  listOrientation="left"
                  reviewStates={CLIP_REVIEW_STATES}
                />
              </IconButton>
            </div>
          </Tooltip>
        )}
        {onRelaunch && (
          <Tooltip help={t('Relanzar')} grow>
            <IconButton size={size} color={iconButtonColor} onClick={() => onRelaunch(item.id)}>
              <AiRedoIcon size={IconSizeEnum.SM} />
            </IconButton>
          </Tooltip>
        )}
        {onDownload && (
          <Tooltip help={t('Descargar')} grow>
            <IconButton
              size={size}
              color={iconButtonColor}
              disabled={item.state !== 'generated'}
              onClick={() => onDownload(item.presigned_url || '')}
            >
              <DownloadIcon size={IconSizeEnum.SM} className="relative top-[1px]" />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip help={t('Eliminar')} grow>
            <IconButton size={size} color={iconButtonColor} onClick={() => onDelete(item.id)}>
              <TrashDeleteIcon size={IconSizeEnum.SM} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </>
  );
}
